import * as React from 'react'
// components
import Grid from '@mui/material/Grid2'
import { Avatar, AvatarsList } from 'components/UI'
// context
import { TasksListBLContext } from 'containers/StrategyActions/components/TasksList/layers/BL'
import { StrategyActionsBLContext } from 'containers/StrategyActions/layers/BL'
// constants
import { ENTITIES, OBJECT_ENTITY_NAMES } from 'constants/index'
import { TASK_COMPLETENESS, TASKS_TYPES } from 'constants/home'
import { REVIEW_STATUSES } from 'constants/review'
// hooks
import { useCurrentUser } from 'hooks'
// themes
import { colors } from 'themes'
import { Chip, ReviewIcon } from 'themes/views/tables'
// types
import { ITask } from 'containers/StrategyActions/components/TasksList/layers/API'
// icons
import WatchLaterIcon from '@mui/icons-material/WatchLaterOutlined'
// views
import { Description, Details, InnerWrapper, TaskPath, Wrapper } from './views'
import { AvatarWrapper, Button, ButtonLabel, ProgressIndicator } from '../../views'
// utils
import moment from 'moment'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import { getPlanTypeLevel } from 'utils/objective'
import { getChipColor, getIconByEntity, isCurrentUser, safeGetOr } from 'utils/index'

const ICON_STYLE = { color: colors.white }
const GRID_STYLE = { width: '50%', flexGrow: 1 }

interface Props {
  task: ITask
}

const Task: React.FC<Props> = ({ task }) => {
  const { isGroupedByPlan } = React.useContext(StrategyActionsBLContext)
  const { onActionButtonClick, onTaskClick, plan } = React.useContext(TasksListBLContext)

  const { actionModifyAction, kpiAddKPIResult, objectiveModifyObjective } = get(task, 'currentPermissions', {})

  const user = useCurrentUser()

  // constants
  const entityType = React.useMemo(() => {
    if (task.entityType === ENTITIES.PLAN_OBJECTIVE) {
      return getPlanTypeLevel(plan, task).description.toUpperCase()
    } else if (task.entityType === ENTITIES.PLAN) {
      return task.entityType
    } else if (task.entityType === ENTITIES.PLAN_KPI) {
      return OBJECT_ENTITY_NAMES.KPI.toUpperCase()
    } else {
      return task.entityType.replace('PLAN_', '')
    }
  }, [task])

  const isReview = task.type === TASKS_TYPES.REVIEW
  const isApproval = task.type === TASKS_TYPES.APPROVAL
  const isAction = task.type === TASKS_TYPES.ACTION
  const isKPI = task.type === TASKS_TYPES.PERIOD
  const isObjective = task.type === TASKS_TYPES.ACTION && task.entityType === ENTITIES.PLAN_OBJECTIVE

  const isCurrentUserTask = task.assignedPersons.some((assignee) => {
    const assigneeId = get(assignee, 'id')

    return isCurrentUser(assigneeId, user)
  })

  const reviewers = safeGetOr<any, any, any>([], 'scheduledReview.scheduledReviewers')(task.review)

  const canCompleteReview = isCurrentUserTask && isReview && moment().isSameOrAfter(task.review.startPeriod, 'day')

  const canCompleteAction =
    (isAction && !isObjective && actionModifyAction) || (isObjective && objectiveModifyObjective)

  const canCompleteKPI = isKPI && kpiAddKPIResult && task.period

  // approval tasks are built only for the review's approver
  const canComplete = canCompleteReview || canCompleteAction || canCompleteKPI || isApproval

  const status = React.useMemo(() => {
    if (isAction) {
      return task.action.completionPercentage === 100 ? TASK_COMPLETENESS.COMPLETE : TASK_COMPLETENESS.IN_PROGRESS
    }
    if (isKPI) {
      return !isEmpty(get(task, 'period.capturedResult')) ? TASK_COMPLETENESS.COMPLETE : TASK_COMPLETENESS.IN_PROGRESS
    }
    if (isReview) {
      return get(task, 'review.status', REVIEW_STATUSES.NOT_STARTED)
    }
    if (isApproval) {
      return TASK_COMPLETENESS.AWAITING_APPROVAL
    }
  }, [task])

  const taskIcon = getIconByEntity(task.entityType)

  // methods
  const handleActionButtonClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    onActionButtonClick(task)
  }

  const handleOnTaskClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    onTaskClick(task)
  }

  return React.useMemo(
    () => (
      <Wrapper canComplete={canComplete} isGroupedByPlan={isGroupedByPlan} onClick={handleOnTaskClick}>
        <InnerWrapper container alignItems={'center'}>
          <Grid>{taskIcon}</Grid>

          <Grid container style={GRID_STYLE}>
            <Description>{task.description}</Description>
            <Details container>
              <Grid>{entityType}</Grid>
              {task.type !== TASKS_TYPES.ACTION && <Grid>{task.type}</Grid>}
              {!isKPI && <ProgressIndicator>{status.replace('_', ' ')}</ProgressIndicator>}
              {isKPI && (
                <ProgressIndicator>{`${moment(task.period.startPeriod).format('D MMM').toLocaleUpperCase()} to ${moment(
                  task.period.endPeriod
                )
                  .format('D MMM')
                  .toLocaleUpperCase()}`}</ProgressIndicator>
              )}
              {task.entityType !== ENTITIES.PLAN && <TaskPath>{`| ${task.path}`}</TaskPath>}
            </Details>
          </Grid>

          <Chip
            rounded
            // an approval has no due state of its own: the due date belongs to the reviewers
            chipColor={isApproval ? colors.gray350 : getChipColor(moment(task.dueDate), status)}
            label={moment(task.dueDate).format('D MMM YY')}
            icon={isReview || isApproval ? <ReviewIcon /> : <WatchLaterIcon style={ICON_STYLE} />}
          />

          {isReview || isApproval ? (
            <AvatarWrapper>
              <AvatarsList list={reviewers} limit={4} />
            </AvatarWrapper>
          ) : (
            <AvatarWrapper>
              <Avatar withHover user={get(task, 'assignedPersons[0]')} />
            </AvatarWrapper>
          )}

          {canComplete && (
            <Button $isFullModal onClick={handleActionButtonClick}>
              <ButtonLabel>
                {isReview && (
                  <>
                    {status === REVIEW_STATUSES.NOT_STARTED && 'START REVIEW'}
                    {status === REVIEW_STATUSES.IN_PROGRESS && 'FINISH REVIEW'}
                    {status === REVIEW_STATUSES.COMPLETE && 'UPDATE REVIEW'}
                  </>
                )}
                {isAction &&
                  (status === TASK_COMPLETENESS.COMPLETE
                    ? `RE-OPEN ${isObjective ? '' : 'ACTION'}`
                    : `COMPLETE ${isObjective ? '' : 'ACTION'}`)}
                {isApproval && 'APPROVE REVIEW'}
                {isKPI && kpiAddKPIResult && (status === TASK_COMPLETENESS.COMPLETE ? 'UPDATE RESULT' : 'ENTER RESULT')}
              </ButtonLabel>
            </Button>
          )}
        </InnerWrapper>
      </Wrapper>
    ),
    [task, isGroupedByPlan]
  )
}

export default Task
